import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, Select, Flex, Button, message } from "antd";
import CreateDepartment from "./Department/CreateDepartment";
import TradeLicenseDrawer from "./TradeLicense/TradeLicenseDrawer";
import CreateBranchLocation from "./BranchLocation/CreateBranchLocation";
import CreateCustomer from "./Customer/CreateCustomer";
import {
  createDimensionAction,
  createFormBuilderAction,
} from "../../../../../../../store/action/admin/dimensionsEntityActions";

const DimensionsCreateDrawer = ({
  open,
  onClose,
  title = "Dimensions",
  dimensionId,
  isCreateMode = true,
}) => {
  const [selectedSection, setSelectedSection] = useState("department");
  const [customerCreated, setCustomerCreated] = useState(false);
  const [createdCustomerDimensionId, setCreatedCustomerDimensionId] =
    useState(null);
  const [customerActiveTab, setCustomerActiveTab] = useState("overview");
  const departmentRef = useRef(null);
  const tradeLicenseRef = useRef(null);
  const branchLocationRef = useRef(null);
  const customerRef = useRef(null);
  const singleEntityState = useSelector((state) => state.getSingleEntityList);
  const getSingleDimensionList = useSelector(
    (state) => state.getSingleDimensionList
  );
  const entityId = singleEntityState?.info?._id;
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!isCreateMode && open && getSingleDimensionList?.info?.dimension) {
      const dimensionType =
        getSingleDimensionList?.info?.dimension?.dimension_type || "";
      if (dimensionType === "department") {
        setSelectedSection("department");
      } else if (dimensionType === "branch") {
        setSelectedSection("branchLocation");
      } else if (dimensionType === "trade_license") {
        setSelectedSection("tradeLicense");
      } else if (dimensionType === "customer") {
        setSelectedSection("customer");
      }
    }
  }, [isCreateMode, open, getSingleDimensionList]);

  React.useEffect(() => {
    if (open) {
      if (selectedSection === "customer") {
        setCustomerActiveTab("overview");
      }
    }
  }, [open, selectedSection]);

  const handleCancel = () => {
    if (selectedSection === "department") {
      departmentRef.current?.cancel?.();
    } else if (selectedSection === "tradeLicense") {
      // nothing
    } else if (selectedSection === "branchLocation") {
      branchLocationRef.current?.cancel?.();
    } else if (selectedSection === "customer") {
      customerRef.current?.cancel?.();
    }
    onClose?.();
  };

  const handleSubmit = async () => {
    try {
      if (isCreateMode && selectedSection === "customer") {
        const activeTab =
          customerActiveTab ||
          customerRef.current?.getActiveTab?.() ||
          "overview";

        if (activeTab === "overview") {
          await customerRef.current?.next?.();
          return;
        }
        if (activeTab === "custom") {
          if (customerCreated && createdCustomerDimensionId) {
            return;
          }

          const values = await customerRef.current?.submit?.();
          if (!values) return;

          const payload = {
            type: "customer",
            ...values,
          };
          const result = await dispatch(createFormBuilderAction(payload));
          message.success("Customer created successfully");
          const dimensionId =
            result?.data?._id ||
            result?.data?.dimension?._id ||
            result?.data?.customer_id;
          setCustomerCreated(true);
          setCreatedCustomerDimensionId(dimensionId);
          onClose?.();
          return;
        }
      }

      let values = null;
      let payload = {};
      if (selectedSection === "department") {
        values = await departmentRef.current?.submit?.();
        if (!values) return;
        payload = {
          type: "department",
          ...values,
        };
      } else if (selectedSection === "tradeLicense") {
        values = await tradeLicenseRef.current?.submit?.();
        if (!values) return;
        payload = {
          type: "trade_license",
          ...values,
        };
      } else if (selectedSection === "branchLocation") {
        values = await branchLocationRef.current?.submit?.();
        if (!values) return;
        payload = {
          type: "branch",
          ...values,
        };
      }

      if (isCreateMode) {
        await dispatch(createDimensionAction(payload));
        message.success("Dimension created successfully");
        onClose?.();
      } else {
        onClose?.();
      }
    } catch (e) {
      if (isCreateMode) {
        const errMsg = e?.message || e || "Failed to create dimension";
        console.error("API Create Dimension Error:", errMsg);
        message.error(errMsg);
      }
    }
  };

  return (
    <Drawer
      title={isCreateMode ? "Dimensions Setup" : "Update Dimension"}
      placement="right"
      onClose={onClose}
      open={open}
      width="100%"
      destroyOnClose
      footer={[
        <Flex
          key="footer"
          gap={8}
          style={{
            justifyContent: "flex-end",
          }}
        >
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {(() => {
              // For customer in create mode, show "Next" on overview tab, "Create" on custom tab
              if (isCreateMode && selectedSection === "customer") {
                const activeTab =
                  customerActiveTab ||
                  customerRef.current?.getActiveTab?.() ||
                  "overview";
                if (activeTab === "overview") {
                  return "Next";
                }
                if (activeTab === "custom") {
                  return "Create";
                }
              }
              // Default button text
              if (
                isCreateMode &&
                selectedSection === "customer" &&
                customerCreated
              ) {
                return "Create";
              }
              return isCreateMode ? "Create" : "Update";
            })()}
          </Button>
        </Flex>,
      ]}
    >
      <div style={{ paddingTop: 8 }}>
        <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>Section:</span>
          <Select
            value={selectedSection}
            onChange={(v) => setSelectedSection(v)}
            disabled={!isCreateMode}
            options={[
              { label: "Department", value: "department" },
              { label: "Trade License", value: "tradeLicense" },
              { label: "Branch Location", value: "branchLocation" },
              { label: "Customer", value: "customer" },
            ]}
            style={{ minWidth: 200 }}
          />
        </Flex>
        {selectedSection === "department" ? (
          <CreateDepartment
            ref={departmentRef}
            onClose={onClose}
            open={open}
            isCreateMode={isCreateMode}
          />
        ) : selectedSection === "tradeLicense" ? (
          <TradeLicenseDrawer
            ref={tradeLicenseRef}
            open={open}
            isCreateMode={isCreateMode}
          />
        ) : selectedSection === "branchLocation" ? (
          <CreateBranchLocation
            ref={branchLocationRef}
            onClose={onClose}
            open={open}
            isCreateMode={isCreateMode}
          />
        ) : selectedSection === "customer" ? (
          <CreateCustomer
            ref={customerRef}
            onClose={onClose}
            open={open}
            isCreateMode={isCreateMode}
            onTabChange={setCustomerActiveTab}
          />
        ) : null}
      </div>
    </Drawer>
  );
};

export default DimensionsCreateDrawer;
